'use client';

import React, { useEffect, useMemo, useRef } from 'react';

import Link from 'next/link';

import { parseCssObject } from '@/app/appcharge/cssObjectToString';
import { CmsImage } from '@/components/CmsImage';
import { POST_THUMBNAIL_SIZES } from '@/lib/images/cmsImage';
import { CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { useEventBatchContext } from '@/hooks/useEventBatch';
import { observerElement } from '@/hooks/useObserver';

import { EEventsType, EPostBlockType, EBlockType } from '@/lib/enums';
import { IPostItem } from '@/lib/types';
import { cn, formatDate } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { Image } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { StyledContentCard } from './style';
import { resolveThumbnailRendering } from './thumbnailRendering';

interface IPostItemProps extends IPostItem {
  uniqueIdentifier: string;
  itemIndex: number;
  /** The page's likely LCP card: its thumbnail is preloaded and fetched at high priority. */
  priority?: boolean;
  className?: string;
  onBlockClick?: () => void;
}

export const PostItem: React.FC<IPostItemProps> = ({
  type,
  className,
  title,
  subTitle,
  thumbnailImage,
  date,
  slug,
  labels,
  uniqueIdentifier,
  contentId,
  itemIndex,
  priority = false,
  onBlockClick,
}) => {
  const t = useTranslations();
  const { initialBoot } = useAppState();
  const portalTheme = initialBoot?.theme;
  const contentCard = portalTheme?.contentCard;
  const contentCardCss = useMemo(() => parseCssObject(contentCard?.css), [contentCard?.css]);
  // Publishers restyle the thumbnail via `.card-image { background-* }` custom CSS. Reproduce
  // that on an optimized <img>; only CSS that swaps the image keeps the CSS-background path.
  const thumbnailRendering = useMemo(() => resolveThumbnailRendering(contentCardCss), [contentCardCss]);
  const paintsThumbnailAsBackground = thumbnailRendering.mode === 'background';

  const isNews = type === EBlockType.NEWS;
  const formattedDate = formatDate(date);

  const itemRef = useRef<HTMLDivElement>(null);
  const { sendEvent, setEventType } = useEventBatchContext();

  useEffect(() => {
    if (!itemRef?.current || !sendEvent) return;

    const postTypes = [EBlockType.NEWS, EBlockType.ARTICLE];
    if (!postTypes.includes(type as EBlockType)) return;

    setEventType(EEventsType.PORTAL_CONTENT_DISPLAYED);

    observerElement(
      itemRef.current,
      {
        id: contentId,
        type,
        index: itemIndex,
        title,
        slug,
      },
      sendEvent,
      uniqueIdentifier
    );
  }, [itemRef?.current, uniqueIdentifier, contentId, itemIndex, type, title, slug]);

  if (!portalTheme) return null;

  return (
    <StyledContentCard
      ref={itemRef}
      $contentCard={contentCard}
      $customCss={contentCardCss}
      $thumbnailImage={paintsThumbnailAsBackground ? thumbnailImage : undefined}
      onClick={onBlockClick}
      data-testid="BlockItem-Card-Container"
      className={cn(
        'postItem card-container flex w-full flex-col gap-y-2.5 rounded-[20px] border-none',
        isNews
          ? 'w-full lg:flex-row lg:items-center lg:gap-x-[42px]'
          : 'w-full flex-1 shrink-0 lg:max-w-[50%]',
        className
      )}
    >
      <div
        data-testid="BlockItem-Image"
        className={cn(
          'card-image relative mx-5 mt-5 flex h-[267px] items-center justify-center rounded-[10px] bg-cover bg-center bg-no-repeat p-28 lg:h-[322px]',
          !thumbnailImage && 'bg-zinc-100 shadow-post-shadow',
          thumbnailImage && 'shadow-none',
          isNews && 'lg:mb-5 lg:mr-0 lg:w-full lg:max-w-[472px]'
        )}
      >
        {thumbnailImage && thumbnailRendering.mode === 'image' && (
          <CmsImage
            data-testid="BlockItem-Thumbnail"
            src={thumbnailImage}
            alt=""
            fill
            sizes={POST_THUMBNAIL_SIZES}
            priority={priority}
            className={cn(
              'rounded-[inherit]',
              thumbnailRendering.objectFit === 'contain' ? 'object-contain' : 'object-cover'
            )}
            style={
              thumbnailRendering.objectPosition
                ? { objectPosition: thumbnailRendering.objectPosition }
                : undefined
            }
          />
        )}
        {labels && labels.length > 0 && (
          <div className="absolute left-3.5 top-3.5 flex flex-wrap gap-2">
            {labels.map((label, index) => (
              <div
                key={index}
                data-testid={`BlockItem-Label-${label}`}
                className="card-badge rounded-md bg-white/50 px-10 py-3 text-[15px] font-semibold text-black"
              >
                {label}
              </div>
            ))}
          </div>
        )}
        {!thumbnailImage && <Image size={64} className="text-zinc-400" />}
      </div>

      {!isNews && (
        <div className="card-divider hidden h-0.5 [background:repeating-linear-gradient(90deg,#F3BDB2_0_8px,transparent_0_15px)_0_0/15px_1px_repeat-x,repeating-linear-gradient(90deg,#FFEDE4_0_8px,transparent_0_15px)_0_1px/15px_1px_repeat-x]" />
      )}

      <CardContent
        className={cn(
          'card-content mx-5 mb-5 flex flex-grow flex-col gap-2.5 p-0',
          isNews && 'lg:ml-0 lg:mt-5'
        )}
      >
        <div
          data-testid="BlockItem-Date"
          className="card-date text-[14px] md:text-[15px] font-normal uppercase leading-[30px]"
        >
          {formattedDate}
        </div>
        <CardTitle
          data-testid="BlockItem-Title"
          className="card-title text-[20px] md:text-2xl font-bold leading-[32px]"
        >
          {title}
        </CardTitle>
        <CardDescription
          data-testid="BlockItem-Description"
          className="card-body line-clamp-3 min-h-[72px] p-0 text-base md:text-lg font-medium leading-6"
        >
          {subTitle}
        </CardDescription>
        <Link
          href={`/content/${slug}`}
          data-testid="BlockItem-ReadMoreButton"
          className={cn(
            'card-button inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl px-8 text-[18px] md:text-xl font-black text-white shadow-post-shadow ring-offset-background transition-colors hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
            isNews && 'lg:mt-6'
          )}
        >
          {t('portal.readMore')}
        </Link>
      </CardContent>
    </StyledContentCard>
  );
};
